/**
 * Integrytas Group - Common Security Module (CSM)
 * Remember-me persistence helper.
 *
 * Behavior:
 *  - When the user opts in, we set a long-lived `csm.remember` cookie. The
 *    Supabase client already mirrors the auth session into localStorage
 *    (see supabase_client.ts), so on the next visit the SDK will pick the
 *    session back up automatically and `useAuthSession` re-hydrates with
 *    no prompt.
 *  - When the user opts out, we install a `beforeunload` guard that wipes
 *    the persisted Supabase session as soon as the tab/browser closes,
 *    forcing a fresh sign-in next time.
 *  - That guard cannot tell a tab CLOSING from the app reloading itself, and
 *    treating the two alike would sign the user out of any in-app reload (the
 *    demo/production switch does exactly that). beginTrustedReload() marks the
 *    unload as ours so the session survives it; closing the tab still wipes.
 */

const COOKIE_NAME = 'csm.remember';
const STORAGE_KEY = 'csm.supabase.auth';        // matches supabase_client storageKey
const ONE_YEAR    = 60 * 60 * 24 * 365;

/**
 * Returns the `Secure` attribute suffix only when the page is served over
 * HTTPS. Browsers reject Secure cookies on http://localhost during dev,
 * so we omit the flag there and add it automatically in production.
 *
 * Note on the v14 spec: the standard calls for "secure, HttpOnly cookies."
 * `HttpOnly` cannot be set from client JavaScript (browsers ignore the
 * attribute on `document.cookie`), and only a server can issue HttpOnly
 * cookies via the `Set-Cookie` response header. In this SPA the Supabase
 * session token lives in localStorage (per supabase-js defaults); this
 * cookie is only a preference flag indicating that the user opted in to
 * being remembered. To meet the standard literally a Backend-For-Frontend
 * proxy would need to own the auth cookie. The `Secure` flag is the
 * strongest hardening we can apply from the client.
 */
function secureAttr(): string {
  if (typeof window === 'undefined') return '';
  return window.location.protocol === 'https:' ? '; Secure' : '';
}

export function setRememberMe(on: boolean): void {
  if (on) {
    document.cookie =
      `${COOKIE_NAME}=Y; max-age=${ONE_YEAR}; path=/; SameSite=Lax${secureAttr()}`;
  } else {
    document.cookie = `${COOKIE_NAME}=; max-age=0; path=/; SameSite=Lax${secureAttr()}`;
  }
}

export function isRememberMe(): boolean {
  return document.cookie
    .split(';')
    .some(c => c.trim().startsWith(`${COOKIE_NAME}=Y`));
}

let unloadGuardInstalled = false;
let trustedReload = false;

/**
 * Declare that the app itself is about to reload the page, so the unload guard
 * leaves the session alone. Call it immediately before location.reload().
 *
 * The flag clears itself shortly after: if the reload does not actually happen
 * the page keeps living, and a stale flag would silently disable the guard for
 * the rest of the session — the one thing it exists to prevent.
 */
export function beginTrustedReload(): void {
  trustedReload = true;
  setTimeout(() => { trustedReload = false; }, TRUSTED_RELOAD_WINDOW_MS);
}

/** How long an announced reload stays trusted before the guard re-arms. */
const TRUSTED_RELOAD_WINDOW_MS = 10_000;

/**
 * Idempotent. Wipes the persisted Supabase session on tab close UNLESS the
 * remember-me cookie is set. Safe to call on every app boot.
 */
export function installRememberMeUnloadGuard(): void {
  if (unloadGuardInstalled || typeof window === 'undefined') return;
  window.addEventListener('beforeunload', () => {
    if (!trustedReload && !isRememberMe()) {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    }
  });
  unloadGuardInstalled = true;
}

export function clearRememberMe(): void {
  setRememberMe(false);
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}
